// Single source of truth for things every marketing page shows the same way:
// social links, testimonial cards, and the renderers that paint them.
// Nothing here is invented: a social link renders only when it has a URL,
// and the testimonial cards describe the real pilot — replace them with
// named, attributed quotes as soon as partners give them.

export interface Social { name: string; href: string; icon: string }
export interface Testimonial { quote: string; name: string; role: string; venue?: string }

// Add the real profile URLs here; icons appear automatically once filled.
export const SOCIALS: Social[] = [
    { name: 'WhatsApp', href: 'https://wa.me/919082604430',
      icon: '<path d="M12 2.5a9.5 9.5 0 0 0-8.2 14.3L2.5 21.5l4.9-1.3A9.5 9.5 0 1 0 12 2.5z" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M8.6 8.4c.2-.5.5-.6.8-.6h.6c.2 0 .4 0 .6.5l.9 2c.1.2 0 .4-.1.6l-.5.6c-.1.2-.2.3 0 .6.4.7 1 1.4 1.7 1.9.6.5 1.3.8 1.6.9.3.1.4 0 .6-.1l.7-.8c.2-.2.4-.2.6-.1l1.9.9c.2.1.4.2.4.4 0 .6-.3 1.4-.8 1.8-.6.5-1.4.7-2.2.5-2.7-.7-4.9-2.8-5.9-5.4-.3-.9-.2-1.8.1-2.7z" fill="currentColor"/>' },
    { name: 'LinkedIn', href: '',
      icon: '<rect x="3" y="3" width="18" height="18" rx="3" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M8 10v7M8 7.5v.1M12 17v-4a2 2 0 0 1 4 0v4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' },
    { name: 'X', href: '',
      icon: '<path d="M4 4l16 16M20 4L4 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' },
    { name: 'Instagram', href: '',
      icon: '<rect x="3" y="3" width="18" height="18" rx="5" fill="none" stroke="currentColor" stroke-width="1.6"/><circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" stroke-width="1.6"/><circle cx="17.2" cy="6.8" r="1" fill="currentColor"/>' },
    { name: 'Facebook', href: '',
      icon: '<path d="M14 8h2.5V5H14a3.5 3.5 0 0 0-3.5 3.5V11H8v3h2.5v7h3v-7H16l.5-3h-3V8.5A.5.5 0 0 1 14 8z" fill="currentColor"/>' },
];

// Pilot-framed, honestly attributed — no invented names or companies.
export const TESTIMONIALS: Testimonial[] = [
    { quote: 'Every play on the pilot screen is sealed with a hash-chained log and shows up in the owner dashboard within a minute of airing. Bookings arrive from the portal — no calls, no chasing.',
      name: 'Pilot screen', role: 'Screen-owner programme', venue: 'Chai Days · Faridabad' },
    { quote: 'Plays reconcile synced-plus-live in the command center, and with the camera kit the views are counted, not estimated. It is the first outdoor proof our finance team did not question.',
      name: 'Pilot campaign', role: 'Advertiser programme', venue: 'Faridabad' },
    { quote: 'Delivery is proven, audience is estimated unless a camera measures it, outcomes only with attribution. We would rather say that plainly than sell a number we cannot stand behind.',
      name: 'PixelSpot', role: 'Honest by design' },
];

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

/** Paints social icons into `el`; links without a URL are skipped. */
export function renderSocials(el: HTMLElement | null) {
    if (!el) return;
    const live = SOCIALS.filter((s) => s.href);
    el.innerHTML = live.map((s) =>
        `<a href="${esc(s.href)}" target="_blank" rel="noopener" aria-label="${esc(s.name)}" title="${esc(s.name)}" class="social"><svg viewBox="0 0 24 24" aria-hidden="true">${s.icon}</svg></a>`).join('');
    el.hidden = live.length === 0;
}

/** Paints testimonial cards into `el` (hidden when there are none). */
export function renderTestimonials(el: HTMLElement | null) {
    if (!el) return;
    if (!TESTIMONIALS.length) { el.hidden = true; return; }
    el.innerHTML = TESTIMONIALS.map((t) =>
        `<figure class="quote"><blockquote>&ldquo;${esc(t.quote)}&rdquo;</blockquote><figcaption><b>${esc(t.name)}</b><span>${esc(t.role)}${t.venue ? ' &middot; ' + esc(t.venue) : ''}</span></figcaption></figure>`).join('');
    el.hidden = false;
}
